import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { keyframes } from '@mui/system';
import VideocamIcon from '@mui/icons-material/Videocam';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import type { ServiceStatus } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Pulsing dot animation for the LIVE indicator
// ---------------------------------------------------------------------------

const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(1.3);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DashboardHeaderProps {
  /** Overall system health status from GET /health */
  systemStatus: ServiceStatus;
  /** Whether the WebSocket connection is active */
  socketConnected: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Dashboard title bar rendered directly on `background.default` (no Paper wrapper).
 *
 * Displays:
 *  - VideocamIcon + "CANvision Dashboard" heading
 *  - LIVE / Disconnected status Chip with a pulsing dot animation
 */
export function DashboardHeader({ systemStatus, socketConnected }: DashboardHeaderProps) {
  const isLive = socketConnected && systemStatus !== 'down';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      {/* Left: icon + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VideocamIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight={600}>
          CANvision Dashboard
        </Typography>
      </Box>

      {/* Right: connection status chip with pulsing dot */}
      <Chip
        icon={
          <FiberManualRecordIcon
            sx={{
              fontSize: 12,
              ...(isLive && {
                animation: `${pulse} 1.5s ease-in-out infinite`,
              }),
            }}
          />
        }
        label={isLive ? 'LIVE' : 'Disconnected'}
        size="small"
        color={isLive ? 'success' : 'error'}
        variant="outlined"
      />
    </Box>
  );
}

export default DashboardHeader;
